/*
 * Create Tournament form — web port of CreateTournamentScreen.
 *
 * On success, sets activeEvent and navigates to /app/tournaments/:id.
 * Validation mirrors the mobile screen so the API never sees nonsense.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { WINNING_RULES, VISIBILITY_OPTIONS } from './_shared';
import type { WinningRuleType, EventVisibility } from '@golf-app/shared';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const POINTS_PRESETS = [0.5, 1, 2, 3];

export default function CreateTournament() {
  const navigate = useNavigate();
  const setActiveEvent = useTournamentStore((s) => s.setActiveEvent);

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pointsPerMatch, setPointsPerMatch] = useState('1');
  const [winningRule, setWinningRule] = useState<WinningRuleType>('most_points_wins');
  const [visibility, setVisibility] = useState<EventVisibility>('private');
  const [teamCount, setTeamCount] = useState<2 | 3 | 4>(2);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (name.trim().length < 2) {
      setError('Please enter an event name (at least 2 characters).');
      return;
    }
    if (startDate && !DATE_RE.test(startDate.trim())) {
      setError('Use the format YYYY-MM-DD for the start date.');
      return;
    }
    if (endDate && !DATE_RE.test(endDate.trim())) {
      setError('Use the format YYYY-MM-DD for the end date.');
      return;
    }
    const points = parseFloat(pointsPerMatch);
    if (!Number.isFinite(points) || points <= 0) {
      setError('Points per match must be a positive number.');
      return;
    }

    setSubmitting(true);
    try {
      const event = await api.createTournament({
        name: name.trim(),
        locationName: location.trim() || undefined,
        startDate: startDate.trim() || undefined,
        endDate: endDate.trim() || undefined,
        defaultPointsPerMatch: points,
        winningRuleType: winningRule,
        visibility,
        teams: Array.from({ length: teamCount }, (_, index) => ({
          name: ['Team Red', 'Team Blue', 'Team Green', 'Team Gold'][index] ?? `Team ${index + 1}`,
        })),
      });
      setActiveEvent(event);
      navigate(`/app/tournaments/${event.id}`, { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell back={{ to: '/app/tournaments', label: 'Tournaments' }}>
      <section className="t-hero">
        <h1>New Team Cup Event</h1>
        <p>Build two or more teams, assign full rosters, and run match play or Team Stableford.</p>
      </section>

      {error && <div className="t-error">{error}</div>}

      <form className="t-form" onSubmit={handleSubmit}>
        <div className="t-field">
          <label htmlFor="t-name" className="t-label">Event name</label>
          <input
            id="t-name"
            className="t-input"
            placeholder="e.g. The Lake House Cup"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
          />
        </div>

        <div className="t-field">
          <label htmlFor="t-loc" className="t-label">Location</label>
          <input
            id="t-loc"
            className="t-input"
            placeholder="e.g. Pinehurst, NC"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={120}
          />
        </div>

        <div className="t-field-row">
          <div className="t-field">
            <label htmlFor="t-start" className="t-label">Start date</label>
            <input
              id="t-start"
              type="date"
              className="t-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onClick={(e) => {
                // Open the OS calendar wherever the user clicks in the input,
                // not just on the tiny calendar icon. showPicker is supported
                // in Chrome 99+, Firefox 101+, Safari 16+ — safe to call.
                try { (e.currentTarget as HTMLInputElement).showPicker(); } catch {}
              }}
            />
          </div>
          <div className="t-field">
            <label htmlFor="t-end" className="t-label">End date</label>
            <input
              id="t-end"
              type="date"
              className="t-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              onClick={(e) => {
                try { (e.currentTarget as HTMLInputElement).showPicker(); } catch {}
              }}
            />
          </div>
        </div>

        <div className="t-field">
          <div className="t-label">Starting teams</div>
          <div className="t-chip-row">
            {([2, 3, 4] as const).map((count) => (
              <button key={count} type="button" className={`t-chip ${teamCount === count ? 't-chip-active' : ''}`} onClick={() => setTeamCount(count)}>
                {count} teams
              </button>
            ))}
          </div>
          <p className="t-hint">Add more teams later from Teams & Captains. Rosters have no per-team player limit.</p>
        </div>

        <div className="t-field">
          <div className="t-label">Points per match</div>
          <div className="t-chip-row">
            {POINTS_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={`t-chip ${parseFloat(pointsPerMatch) === p ? 't-chip-active' : ''}`}
                onClick={() => setPointsPerMatch(String(p))}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="t-field">
          <div className="t-label">Winning rule</div>
          {WINNING_RULES.map((r) => (
            <button
              key={r.value}
              type="button"
              className={`t-option ${winningRule === r.value ? 't-option-selected' : ''}`}
              onClick={() => setWinningRule(r.value)}
            >
              <span className="t-option-radio">
                {winningRule === r.value && <span className="t-option-radio-dot" />}
              </span>
              <span>
                <p className={`t-option-label ${winningRule === r.value ? 't-option-label-selected' : ''}`}>{r.label}</p>
                <p className="t-option-hint">{r.hint}</p>
              </span>
            </button>
          ))}
        </div>

        <div className="t-field">
          <div className="t-label">Visibility</div>
          {VISIBILITY_OPTIONS.map((v) => (
            <button
              key={v.value}
              type="button"
              className={`t-option ${visibility === v.value ? 't-option-selected' : ''}`}
              onClick={() => setVisibility(v.value)}
            >
              <span className="t-option-radio">
                {visibility === v.value && <span className="t-option-radio-dot" />}
              </span>
              <span>
                <p className={`t-option-label ${visibility === v.value ? 't-option-label-selected' : ''}`}>{v.label}</p>
                <p className="t-option-hint">{v.hint}</p>
              </span>
            </button>
          ))}
        </div>

        <p className="t-hint">
          {teamCount} teams are created automatically. You can add more, rename them, pick colors,
          assign captains, and build the roster from the event home next.
        </p>

        <button
          type="submit"
          className="t-btn t-btn-primary t-btn-block"
          disabled={submitting}
        >
          {submitting ? 'Creating…' : 'Create Event'}
        </button>
      </form>
    </AppShell>
  );
}
